// src/core/frame_diff.ts
// Frame-level state transition engine for replay highlighting.
//
// Purpose:
//   - Detect what changed between two consecutive frames
//   - Used for replay UI highlighting (show what changed, not full re-render)
//   - NOT for state reconstruction (FrameCache handles that)
//
// Input:  prevFrame, nextFrame (UIFrame full snapshots)
// Output: diff = { sections: { market, signals, broker, execution, metrics, gate_status } }

import type { UIFrame } from './ui_contract';

export interface FieldDiff<T = unknown> {
  field: string;
  prev: T;
  next: T;
}

export interface SectionDiff {
  changed: boolean;
  fields: FieldDiff[];
}

export interface FrameDiff {
  hasChanges: boolean;
  sections: {
    market: SectionDiff;
    signals: SectionDiff;
    broker: SectionDiff;
    execution: SectionDiff;
    metrics: SectionDiff;
    gate_status: SectionDiff;
  };
}

// ---- Market ----

function diffMarket(prev: UIFrame['market'], next: UIFrame['market']): SectionDiff {
  const fields: FieldDiff[] = [];

  // Symbols
  if (JSON.stringify(prev.symbols) !== JSON.stringify(next.symbols)) {
    fields.push({ field: 'symbols', prev: prev.symbols, next: next.symbols });
  }

  // Quotes
  const prevQuotes = prev.quotes ?? {};
  const nextQuotes = next.quotes ?? {};
  const prevQuotesStr = JSON.stringify(prevQuotes);
  const nextQuotesStr = JSON.stringify(nextQuotes);
  if (prevQuotesStr !== nextQuotesStr) {
    // Find which quotes changed
    const allSymbols = new Set([...Object.keys(prevQuotes), ...Object.keys(nextQuotes)]);
    const changedQuotes: Record<string, { prev: unknown; next: unknown }> = {};
    for (const sym of allSymbols) {
      const p = JSON.stringify(prevQuotes[sym]);
      const n = JSON.stringify(nextQuotes[sym]);
      if (p !== n) {
        changedQuotes[sym] = { prev: prevQuotes[sym], next: nextQuotes[sym] };
      }
    }
    fields.push({ field: 'quotes', prev: changedQuotes, next: null });
  }

  // Movers
  if (JSON.stringify(prev.movers) !== JSON.stringify(next.movers)) {
    fields.push({ field: 'movers', prev: prev.movers, next: next.movers });
  }

  return { changed: fields.length > 0, fields };
}

// ---- Signals ----

function diffSignals(prev: UIFrame['signals'], next: UIFrame['signals']): SectionDiff {
  const fields: FieldDiff[] = [];

  if (JSON.stringify(prev.top) !== JSON.stringify(next.top)) {
    fields.push({ field: 'top', prev: prev.top, next: next.top });
  }
  if (JSON.stringify(prev.buy_signals) !== JSON.stringify(next.buy_signals)) {
    fields.push({ field: 'buy_signals', prev: prev.buy_signals, next: next.buy_signals });
  }
  if (JSON.stringify(prev.sell_signals) !== JSON.stringify(next.sell_signals)) {
    fields.push({ field: 'sell_signals', prev: prev.sell_signals, next: next.sell_signals });
  }

  return { changed: fields.length > 0, fields };
}

// ---- Broker ----

function diffBroker(prev: UIFrame['broker'], next: UIFrame['broker']): SectionDiff {
  const fields: FieldDiff[] = [];

  if (JSON.stringify(prev.status) !== JSON.stringify(next.status)) {
    fields.push({ field: 'status', prev: prev.status, next: next.status });
  }
  if (JSON.stringify(prev.accounts) !== JSON.stringify(next.accounts)) {
    fields.push({ field: 'accounts', prev: prev.accounts, next: next.accounts });
  }
  if (JSON.stringify(prev.positions) !== JSON.stringify(next.positions)) {
    // Deep diff positions per broker
    const prevPos = prev.positions ?? {};
    const nextPos = next.positions ?? {};
    const allBrokers = new Set([...Object.keys(prevPos), ...Object.keys(nextPos)]);
    const changedBrokers: Record<string, { prev: unknown; next: unknown }> = {};
    for (const broker of allBrokers) {
      const p = JSON.stringify(prevPos[broker]);
      const n = JSON.stringify(nextPos[broker]);
      if (p !== n) {
        changedBrokers[broker] = { prev: prevPos[broker], next: nextPos[broker] };
      }
    }
    fields.push({ field: 'positions', prev: changedBrokers, next: null });
  }

  return { changed: fields.length > 0, fields };
}

// ---- Execution ----

function diffExecution(prev: UIFrame['execution'], next: UIFrame['execution']): SectionDiff {
  const fields: FieldDiff[] = [];

  if (JSON.stringify(prev.active_orders) !== JSON.stringify(next.active_orders)) {
    fields.push({ field: 'active_orders', prev: prev.active_orders, next: next.active_orders });
  }
  if (JSON.stringify(prev.fill_stats) !== JSON.stringify(next.fill_stats)) {
    fields.push({ field: 'fill_stats', prev: prev.fill_stats, next: next.fill_stats });
  }

  return { changed: fields.length > 0, fields };
}

// ---- Metrics ----

function diffMetrics(prev: UIFrame['metrics'], next: UIFrame['metrics']): SectionDiff {
  const fields: FieldDiff[] = [];
  const keys: (keyof typeof prev)[] = [
    'total_events', 'quote_count', 'order_count', 'fill_count',
    'reject_count', 'signal_count', 'buy_signals', 'sell_signals',
    'fill_rate', 'quote_delay_ms',
  ];

  for (const key of keys) {
    if (prev[key] !== next[key]) {
      fields.push({ field: key, prev: prev[key], next: next[key] });
    }
  }

  return { changed: fields.length > 0, fields };
}

// ---- Gate Status ----

function diffGateStatus(prev: UIFrame['gate_status'], next: UIFrame['gate_status']): SectionDiff {
  const fields: FieldDiff[] = [];
  const keys = ['G1', 'G2', 'G3', 'G4'] as const;

  for (const key of keys) {
    if (prev[key] !== next[key]) {
      fields.push({ field: key, prev: prev[key], next: next[key] });
    }
  }

  return { changed: fields.length > 0, fields };
}

// ---- Main Diff Function ----

export function diffFrames(prev: UIFrame, next: UIFrame): FrameDiff {
  const sections = {
    market: diffMarket(prev.market, next.market),
    signals: diffSignals(prev.signals, next.signals),
    broker: diffBroker(prev.broker, next.broker),
    execution: diffExecution(prev.execution, next.execution),
    metrics: diffMetrics(prev.metrics, next.metrics),
    gate_status: diffGateStatus(prev.gate_status, next.gate_status),
  };

  const hasChanges = Object.values(sections).some(s => s.changed);

  return { hasChanges, sections };
}

// ---- Utility ----

/** Returns true if the frame changed at all */
export function hasChanges(diff: FrameDiff): boolean {
  return diff.hasChanges;
}

/** Returns a flat list of all changed field paths */
export function getChangedFields(diff: FrameDiff): string[] {
  const result: string[] = [];
  for (const [section, sectionDiff] of Object.entries(diff.sections)) {
    if (sectionDiff.changed) {
      for (const field of sectionDiff.fields) {
        result.push(`${section}.${field.field}`);
      }
    }
  }
  return result;
}
